import { useState, useEffect } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,
} from "@mui/material";
// components
import { Log, sendDataWithPost } from "../../services/services";
import api from "../../services/api";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../../components/TableRow";
import { isEmpty } from "lodash";

import FormSearch from "../../components/FormSearch";
import moment from "moment";
import * as Yup from "yup";
import CircularLoader from "../../components/circularLoader";
import { CustomTableCell } from "../../components/TableCell";
import Pagination from "../../components/Pagination";
import { acquirerList, DEFAULT_MIN_PAYOUT } from "../../utils/constants";

// ----------------------------------------------------------------------

const defaultValue = {
  batch_date: null,
  is_settled: true,
  start_datetime: moment().subtract(6, 'days'),
  end_datetime: moment()
};

const validationSchema = Yup.object().shape({
  batch_date: Yup.date().nullable().typeError("Invalid Batch Date"),
  start_datetime: Yup.date().nullable().typeError("Invalid Start Date"),
  end_datetime: Yup.date().nullable().typeError("Invalid End Date"),
  payout_date: Yup.date().nullable().typeError("Invalid Payout Date"),
}).test("date-range", "Start date cannot be greater than end date", function (values) {
  const { start_datetime, end_datetime } = values;
  if (start_datetime && end_datetime && moment(start_datetime).isAfter(end_datetime)) {
    return false;
  }
  return true;
});

const TABLE_HEAD = [
  { id: "transaction_id", label: "Transaction ID" },
  { id: "transaction_datetime", label: "Transaction Time" },
  { id: "transaction_currency", label: "Currency" },
  { id: "transaction_amount", label: "Transaction Amount" },
  { id: "merchant_gst", label: "Merchant GST" },
  { id: "merchant_gst_amount", label: "Merchant GST Amount" },
  { id: "paid_amount", label: "Paid Amount" },
  { id: "payment_brand", label: "Payment Brand" },
  { id: "transaction_type", label: "Type" },
  { id: "transaction_state", label: "State" },
  { id: "merchant_id", label: "Merchant ID" },
  { id: "merchant_name", label: "Merchant Name" },
  { id: "terminal_id", label: "Terminal ID" },
  { id: "acquirer", label: "Acquirer" },
  { id: "source", label: "Source" },
  { id: "batch_date", label: "Batch Date" },
  { id: "payout_date", label: "Payout Date" },
  { id: "payout_status", label: "Payout Status" },
];

export default function Settlement() {
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });
  const [filter, setFilter] = useState({});
  const [errors, setErrors] = useState({});

  // ------------------ filterStuffies --------------------//
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });

  //Dialogs
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryList({
      page: newPage + 1,
      page_size: pagination.page_size,
    }, filter);
  };

  const handleChangeRowsPerPage = (event) => {
    queryList({
      page: 1,
      page_size: event.target.value,
    }, filter);
  };

  const queryList = (params, filter) => {
    loaded && setLoaded(false);
    queryPage(params, filter);
  };

  const queryPage = (params, filter) => {
    const queryParams = new URLSearchParams(params || pagination).toString();
    const url = `/payout/settlement/filter_settlement/?${queryParams}`;
    sendDataWithPost(url, filter, setTableData, setLoaded, () => {
      params && setPagination(params);
    });
  };

  const onFilterSubmit = async (fParams) => {
    try {
      await validationSchema.validate(fParams, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const validationErrors = {};
      err.inner?.forEach((e) => {
        if (e.message === "Start date cannot be greater than end date") {
          validationErrors.start_datetime = e.message;
          validationErrors.end_datetime = e.message;
        } else {
          validationErrors[e.path] = e.message;
        }
      });
      setErrors(validationErrors);
      return;
    }

    const { batch_date, start_datetime, end_datetime, payout_date, ...rest } = fParams;

    const newPrams = {
      ...rest,
      batch_date:
        batch_date && moment(batch_date).isValid()
          ? batch_date.format("YYYY-MM-DD")
          : null,
      start_transaction_date:
        start_datetime && moment(start_datetime).isValid()
          ? start_datetime.format("YYYY-MM-DD")
          : null,
      end_transaction_date:
        end_datetime && moment(end_datetime).isValid()
          ? end_datetime.format("YYYY-MM-DD")
          : null,
      acquirer: fParams.acquirer?.id,
      transaction_id: fParams.transaction_id?.trim(),
      terminal_id: fParams.terminal_id?.trim(),
      merchant_name: fParams.merchant_name?.trim(),
      transaction_currency: fParams.transaction_currency?.trim(),
      payout_date:
        fParams.payout_date && moment(fParams.payout_date).isValid()
          ? fParams.payout_date.format("YYYY-MM-DD")
          : null,
      payout_status: fParams.payout_status,
    };
    if (newPrams.is_settled === 'all') delete newPrams.is_settled;
    if (newPrams.payout_status === 'all') delete newPrams.payout_status;
    if (newPrams.transaction_currency === 'all') delete newPrams.transaction_currency;

    const newPagination = {
      ...pagination,
      page: 1,
    };

    Log.info("Search Payout Settlement list", newPrams);
    setFilter(newPrams);
    queryList(newPagination, newPrams);
  };

  const getCSVData = async () => {
    try {
      const data = await api.post(`/payout/settlement/filter_settlement/`, {
        ...filter,
        download: "true",
      });
      return data.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getFileName = () => {
    let name = "PayoutSettlement";
    if (filter.acquirer) {
      const acquirerObj = acquirerList.find(a => a.id === filter.acquirer);
      const acquirerName = acquirerObj ? acquirerObj.name : filter.acquirer;
      name += `-${acquirerName}`;
    }
    if (filter.transaction_currency) {
      name += `-${filter.transaction_currency}`;
    }
    
    if (filter.start_transaction_date && filter.end_transaction_date) {
      name += `-${filter.start_transaction_date}-to-${filter.end_transaction_date}`;
    } else if (filter.start_transaction_date) {
      name += `-from-${filter.start_transaction_date}`;
    } else if (filter.end_transaction_date) {
      name += `-to-${filter.end_transaction_date}`;
    } else {
      name += `-${moment().format("YYYY-MM-DD")}`;
    }
    return name + ".xlsx";
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    onFilterSubmit(defaultValue);
  }, []);

  const isUserNotFound = tableData?.results?.length === 0;

  const filteredList = applySortFilter(
    tableData?.results || [],
    order,
    orderBy
  );

  const schema = [
    {
      type: "daterange",
      label: "Transaction Time",
      param: ["start_datetime", "end_datetime"],
      width: 6
    },
    {
      type: "date",
      label: "Batch Date",
      param: "batch_date",
    },
    {
      type: "text",
      label: "Transaction ID",
      param: "transaction_id",
    },
    {
      type: "text",
      label: "Terminal ID",
      param: "terminal_id",
    },
    {
      type: "text",
      label: "Merchant Name",
      param: "merchant_name",
    },
    {
      type: "search-select",
      label: "Acquirer",
      param: "acquirer",
      options: acquirerList.map(item => ({
        id: item.id,
        label: item.name
      }))
    },
    {
      type: "select",
      label: "Is Settle",
      param: "is_settled",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ],
    },
    {
      type: "date",
      label: "Payout Date",
      param: "payout_date",
    },
    {
      type: "select",
      label: "Payout Status",
      param: "payout_status",
      options: [
        { name: "All", id: "all" },
        { name: "Pending", id: "Pending" },
        { name: "On-hold", id: "On-hold" },
        { name: "Paid", id: "Paid" },
      ]
    },
    {
      type: "select",
      label: "Currency",
      param: "transaction_currency",
      options: [
        { name: "All", id: "all" },
        ...DEFAULT_MIN_PAYOUT.map(item => ({
          id: item.currency,
          name: item.currency
        }))
      ]
    },
  ];

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={true}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
        errors={errors}
        type="PayoutSettlement"
        csv={true}
        filename={getFileName()}
        getCSVData={tableData?.results?.length > 0 ? getCSVData : false}
      />
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row, i) => {
                    const {
                      transaction_id,
                      transaction_datetime,
                      transaction_currency,
                      transaction_amount,
                      merchant_gst,
                      merchant_gst_amount,
                      paid_amount,
                      payment_brand,
                      transaction_type,
                      transaction_state,
                      merchant_id,
                      merchant_name,
                      terminal_id,
                      batch_date,
                      payout_date,
                      payout_status,
                      acquirer,
                      source
                    } = row;

                    return (
                      <StyledTableRow hover key={i}>
                        <CustomTableCell field={transaction_id} />
                        <CustomTableCell field={transaction_datetime} />
                        <CustomTableCell field={transaction_currency} />
                        <CustomTableCell field={transaction_amount} />
                        <CustomTableCell field={merchant_gst} />
                        <CustomTableCell field={merchant_gst_amount} />
                        <CustomTableCell field={paid_amount} />
                        <CustomTableCell field={payment_brand} />
                        <CustomTableCell field={transaction_type} />
                        <CustomTableCell field={transaction_state} />
                        <CustomTableCell field={merchant_id} />
                        <CustomTableCell field={merchant_name} />
                        <CustomTableCell field={terminal_id} />
                        <CustomTableCell field={acquirer} />
                        <CustomTableCell field={source} />
                        <CustomTableCell field={batch_date} />
                        <CustomTableCell field={payout_date} />
                        <CustomTableCell field={payout_status} />
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isEmpty(tableData?.results) && (
						<Pagination
							count={tableData.count}
							pagination={pagination}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
    </Box>
  );
}
